import {useCallback, useEffect, useState} from 'react';
import {Avatar, Grid, InputAdornment, makeStyles, TextField, withStyles} from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { useDispatch, useSelector } from "react-redux";
import {
  moveNextPage,
  updateCreatorOnboarding,
  updateDepartment
} from "../../../../store/actions/creator/creatorOnBoarding";
import { updateUserProfile } from "../../../../store/actions";
import UtilService from '../../../../helper/service';
import { useHistory, useLocation } from "react-router";
import OnboardingBtn from "../../../atoms/creator/onboardingEnterBtn";
import {getGravatarImage} from "../../../../store/actions/auth/auth";
import UploadProfilePicture from "../../../atoms/modals/uploadProfilePicture";
import CustomSnackBar from "../experienceManager/participantsDashboard/component/CustomSnackBar";

const CssTextField = withStyles({
  root: {
    marginTop: 30,
    fontSize: 24,
    '& label': {
      fontSize: 16,
      fontFamily: 'roboto-black',
      color: '#697683',
    },
    '& input': {
      fontSize: 24,
      fontFamily: 'roboto-light',
      color: '#8F99A3',
      '@media (max-width: 600px)': {
        fontSize: 16,
      },
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#697683',
      borderBottomWidth: 3,
    },
    '&:hover .MuiInput-underline:before': {
      borderBottomColor: '#697683',
      borderBottomWidth: 3
    },
    '& .MuiInput-underline.Mui-focused:before': {
      borderBottomColor: '#017AEF',
      borderBottomWidth: 3
    },
    '& .MuiInput-underline.Mui-focused:after': {
      borderBottomColor: '#017AEF',
      borderBottomWidth: 3
    },
    "& .MuiInputBase-root.Mui-disabled:before": {
      borderBottom: '3px solid #697683'
    },
    "& .MuiFormLabel-asterisk.MuiInputLabel-asterisk": {
      color: "#ED564A"
    },
    '& .MuiFormLabel-root.Mui-disabled': {
      color: '#697683',
      opacity: 0.7
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: '#8F99A3',
      opacity: 0.7,
      '-webkit-text-fill-color': '#8F99A3',
      '-webkit-opacity': 0.7
    }
  },
})(TextField);

const useStyles = makeStyles(() => ({
  mainTitle: {
    fontSize: 48,
    fontFamily: 'Spartan-bold',
    color: '#212D40',
    textAlign: 'center',
    marginTop: 10,
    marginBottom: 10,
    '@media (max-width: 600px)': {
      fontSize: 25,
    },
  },
  description: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& p': {
      margin: 0,
      maxWidth: 440,
      fontFamily: 'roboto-light',
      fontSize: 24,
      color: '#212D40',
      lineHeight: 1.25,
      textAlign: 'center',
      '@media (max-width: 600px)': {
        fontSize: 15,
      }
    }
  },
  avatarSection: {
    marginTop: 30,
    display: 'flex'
  },
  avatar: {
    height: 120,
    width: 120,
    cursor: 'pointer',
    border: '5px solid #707070'
  },
  profilePic: {
    fontSize: 16,
    fontFamily: 'roboto-black',
    color: '#697683',
  },
  profileDescription: {
    fontFamily: 'roboto-light',
    fontSize: 14,
    color: '#212D40',
    marginTop: 15
  }
}));

interface IProps {
  isTrial: boolean,
  isTeamOwner: boolean,
  loginUserData: any
}

function OnBoardingBackground(props: IProps) {
  const { isTrial, isTeamOwner, loginUserData } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { userPreferences } = useSelector((state: any) => state.creatorOnBoarding);
  const { activepage } = userPreferences;
  const newDepartmentAlias = localStorage.getItem("departmentAlias") ? localStorage.getItem("departmentAlias") : "";
  const {
    userPreferencesData, userProfileData, googleProfilePicture, gravatarImg
  } = useSelector((state: any) => state.authUser);
  const apolloClient = useSelector((state: any) => state.apolloClient);
  const [title, setTitle] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [departmentAlias, setDepartmentAlias] = useState('');
  const [focusField, setFocusField] = useState('');
  const [uploadedProfilePic, setUploadedProfilePic] = useState('');
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const handleUserKeyPress = (event: any) => {
    if (event.keyCode === 13) {
      goNext();
    }
  };

  useEffect(() => {
    let updateLoginUserData = loginUserData;
    if (userProfileData && userProfileData.preferences) {
      const profilePicture = userProfileData.preferences.filter((result:any) => result.preference_name === "PROFILEPIC");
      if (profilePicture.length && profilePicture[0]) {
        updateLoginUserData = {...loginUserData, profile_picture: profilePicture[0].preference_information};
        localStorage.setItem('user_data', JSON.stringify(updateLoginUserData));
      }
    }
    const profilePic = UtilService.getUserProfile(updateLoginUserData.profile_picture, userProfileData, userPreferencesData);
    if (profilePic === '/static/profile.png') {
      if (updateLoginUserData && updateLoginUserData.username && localStorage.getItem('sso_login') === "false") {
        dispatch(getGravatarImage(apolloClient.client, {email: updateLoginUserData.username}))
      }
    } else {
      setUploadedProfilePic(profilePic);
    }
  }, [userPreferencesData, userProfileData]);

  useEffect(() => {
    if (history.location.hash !== '#about') {
      UtilService.addHashTag(history, location.pathname, 'about');
    }
  }, []);

  useEffect(() => {
    if (userPreferences && userPreferences.title.value) {
      setTitle(userPreferences.title.value);
    }
  }, [userPreferences]);

  useEffect(() => {
    if (!!newDepartmentAlias) {
      setDepartmentAlias(newDepartmentAlias);
    } else {
      if (!!loginUserData) {
        loginUserData.department_alias && setDepartmentAlias(loginUserData.department_alias);
      }
    }
  }, [newDepartmentAlias]);

  useEffect(() => {
    if (userProfileData) {
      userProfileData.first_name && setFirstName(UtilService.capitalizeFirstLetter(userProfileData.first_name));
      userProfileData.last_name && setLastName(UtilService.capitalizeFirstLetter(userProfileData.last_name));
    }
  }, [userProfileData]);

  useEffect(() => {
    if (userProfileData) {
      if (!userProfileData.first_name) {
        setFocusField("first_name")
      }
      if (!userProfileData.last_name) {
        setFocusField("last_name")
      }
      if (!loginUserData.department_alias) {
        setFocusField("department_alias")
      }
      if (!title) {
        setFocusField("your_title")
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  });

  const goNext = () => {
    if (onCheckNextPageAbility()) {
      const request = {
        user_id: loginUserData.user_id,
        preference_id: userPreferences.title.id,
        preference_name: "TITLE",
        preference_information: title,
      };
      dispatch(updateCreatorOnboarding(apolloClient.client, request));

      const requestNames = {
        user_id: loginUserData.user_id,
        firstname: firstName,
        middlename: loginUserData.middle_name,
        lastname: lastName,
        mobile_number: loginUserData.mfa_number,
        cognito_username: loginUserData.username,
        mfa_enable: loginUserData.mfa_enabled,
      };
      dispatch(updateUserProfile(apolloClient.client, requestNames));
      localStorage.setItem('firstName', `${firstName}`);
      localStorage.setItem('lastName', `${lastName}`);

      if (isTrial && isTeamOwner) {
        const departmentReq = {
          department_id: Number(loginUserData.department_id),
          department_alias: departmentAlias
        };
        dispatch(updateDepartment(apolloClient.client, departmentReq, (res: any) => {
          if (res) {
            const newDepartmentAlias = res.updateDepartment.department_alias;
            localStorage.setItem('departmentAlias', newDepartmentAlias);
          }
        }))
      }

      if (Number(activepage.value) < 1) {
        const reqActivePage = {
          user_id: loginUserData.user_id,
          preference_id: userPreferences.activepage.id,
          preference_name: 'ACTIVEPAGE',
          preference_information: '1',
        };
        dispatch(updateCreatorOnboarding(apolloClient.client, reqActivePage));
      }

      dispatch(moveNextPage(1))
    }
  };

  const handleDepartmentAlias = (e: any) => {
    const org = e.target.value;
    if (org === ' ' && departmentAlias.length === 0) {
      e.preventDefault();
    } else {
      setDepartmentAlias(org);
      setFocusField("");
    }
  };

  const isFocusOrganization = () => {
    const sso_login = localStorage.getItem('sso_login');
    return sso_login === "true" && isTeamOwner;
  };

  const onChangeFirstName = (e: any) => {
    const value = e.target.value;
    if (value === ' ' && firstName.length === 0) {
      e.preventDefault()
    } else {
      setFirstName(UtilService.capitalizeFirstLetter(value));
      setFocusField("");
    }
  };

  const onChangeLastName = (e: any) => {
    const value = e.target.value;
    if (value === ' ' && lastName.length === 0) {
      e.preventDefault()
    } else {
      setLastName(UtilService.capitalizeFirstLetter(value));
      setFocusField("");
    }
  };

  const onChangeTitle = (e: any) => {
    const value = e.target.value;
    if (value === ' ' && title.length === 0) {
      e.preventDefault()
    } else {
      setTitle(value);
      setFocusField("");
    }
  };

  const getAvatarImage = useCallback(() => {
    if (uploadedProfilePic) {
      return uploadedProfilePic
    } else if (googleProfilePicture) {
      return googleProfilePicture
    } else if (gravatarImg) {
      return gravatarImg;
    }
    return '/static/profile.png'
  }, [uploadedProfilePic, googleProfilePicture, gravatarImg]);

  const onChangeAvatar = () => {
    setOpenUploadModal(true)
  };

  const onCloseUploadModal = () => {
    setOpenUploadModal(false);
  };

  const onFailedImageUploading = () => {
    onCloseUploadModal();
    setShowAlert(true);
  };

  const onCheckNextPageAbility = useCallback(() => {
    const sso_login = localStorage.getItem('sso_login');
    if (sso_login === "false") {
      return !!(title && firstName && lastName && uploadedProfilePic && (isTeamOwner ? departmentAlias : true));
    } else {
      return !!(title && firstName && lastName && (isTeamOwner ? departmentAlias : true));
    }

  }, [title, firstName, lastName, uploadedProfilePic, isTeamOwner, departmentAlias, loginUserData]);

  const checkShowOrganisation = useCallback(() => {
    const sso_login = localStorage.getItem('sso_login');
    if (sso_login === 'false') {
      return !!uploadedProfilePic
    } else {
      return true;
    }
  }, [uploadedProfilePic]);

  return (
    <>
      <div className={classes.mainTitle}>
        Welcome!
      </div>
      <div className={classes.description}>
        <p>
          Take a few seconds to give us a little bit
          of info and we’ll get you right on your way!
        </p>
      </div>
      <Grid container justifyContent={'center'}>
        <Grid item xs={12} md={7}>
          <CssTextField
            autoFocus={(focusField === "first_name") || !isFocusOrganization()}
            margin="normal"
            fullWidth
            id="first_name"
            label="First Name"
            name="first_name"
            value={UtilService.capitalizeFirstLetter(firstName)}
            onChange={(e) => onChangeFirstName(e)}
            required={true}
          />
          {firstName &&
            <CssTextField
              margin="normal"
              fullWidth
              autoFocus={focusField === "last_name"}
              id="last_name"
              label="Last Name"
              name="last_name"
              value={UtilService.capitalizeFirstLetter(lastName)}
              onChange={(e) => onChangeLastName(e)}
              required={true}
            />
          }
          {(firstName && lastName) && (
            <>
              <div className={classes.avatarSection}>
                <Avatar
                  className={classes.avatar}
                  src={getAvatarImage()}
                  alt="Avatar"
                  onClick={onChangeAvatar}
                />
                <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 20 }}>
                  <span className={classes.profilePic}>Profile Picture <span style={{ color: '#ED564A' }}>*</span></span>
                  <div className={classes.profileDescription}>
                    We&apos;ll use this in the invitations you send to people to participate. You can change this at any time.
                  </div>
                </div>
              </div>
              {checkShowOrganisation() && (
                <>
                  {isTrial ? (
                    <CssTextField
                      disabled={!isTeamOwner}
                      autoFocus={(focusField === "department_alias") || isFocusOrganization()}
                      margin="normal"
                      fullWidth
                      id="department_alias"
                      label="Your Organization"
                      name="department_alias"
                      value={departmentAlias}
                      onChange={(e) => { isTeamOwner && handleDepartmentAlias(e) }}
                      InputProps={{
                        endAdornment: (
                          !isTeamOwner &&
                          <InputAdornment position="end">
                            <LockOutlinedIcon style={{ fontSize: 16, color: '#697683' }} />
                          </InputAdornment>
                        ),
                      }}
                      required={true}
                    />
                  ) : (
                    <CssTextField
                      margin="normal"
                      fullWidth
                      id="your_company"
                      label="Your Company"
                      name="your_company"
                      value={loginUserData.customer_display_name}
                      disabled
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <LockOutlinedIcon style={{ fontSize: 16, color: '#697683' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  {(isTeamOwner ? departmentAlias : true) && (
                    <CssTextField
                      margin="normal"
                      fullWidth
                      autoFocus={focusField === "your_title"}
                      id="your_title"
                      label="Your Title"
                      name="your_title"
                      value={title}
                      onChange={(e) => onChangeTitle(e)}
                      required={true}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      </Grid>
      <OnboardingBtn
        title={'Next'}
        hiddenBack
        isRight
        goNext={goNext}
        style={onCheckNextPageAbility() ? {} : { visibility: 'hidden' }}
      />
      <UploadProfilePicture
        open={openUploadModal}
        onClose={onCloseUploadModal}
        onFailed={onFailedImageUploading}
      />
      <CustomSnackBar
        type={'error'}
        message={'Image uploading is failed'}
        open={showAlert}
        onClose={() => setShowAlert(false)}
      />
    </>

  )

}

export default OnBoardingBackground;